import { tr } from '@/lib/i18n-core';
import { Plus, Stethoscope, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { doctorPriceMinor, type DoctorDiscountKind } from '@his/shared';
import { get } from '@/lib/api';
import { money } from '@/lib/utils';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormSection } from '@/components/ui/form-screen';
import { Button, Input, Select } from '@/components/ui/primitives';

export interface ServiceDoctorRow {
  doctorId: string;
  discountKind: DoctorDiscountKind;
  value: string;
}

export interface ServiceDoctorView {
  id: string;
  doctorId: string;
  doctorName: string;
  discountKind: string;
  discountValue: number;
  effectiveFrom: string;
}

interface ClinicianOption {
  id: string;
  name: string;
}

export function rowsFromViews(views: ServiceDoctorView[]): ServiceDoctorRow[] {
  return views.map((view) => ({
    doctorId: view.doctorId,
    discountKind: view.discountKind === 'percent' ? 'percent' : 'fixed',
    value: view.discountKind === 'percent' ? String(view.discountValue) : formatMoneyInput(view.discountValue),
  }));
}

export function discountValueOf(row: ServiceDoctorRow): number {
  return row.discountKind === 'percent' ? Math.max(0, Math.min(100, Number(row.value) || 0)) : Math.max(0, minorFromInput(row.value));
}

export function doctorRowsBody(rows: ServiceDoctorRow[]): { doctorId: string; discountKind: DoctorDiscountKind; discountValue: number }[] {
  return rows
    .filter((row) => row.doctorId)
    .map((row) => ({ doctorId: row.doctorId, discountKind: row.discountKind, discountValue: discountValueOf(row) }));
}

export function ServiceDoctorsSection({
  rows,
  onChange,
  basePriceMinor,
}: {
  rows: ServiceDoctorRow[];
  onChange: (rows: ServiceDoctorRow[]) => void;
  basePriceMinor: number;
}) {
  const clinicians = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () => (await get<{ data: ClinicianOption[] }>('/org/clinicians?kind=bookable&limit=200')).data,
  });
  const options = clinicians.data ?? [];
  const chosen = new Set(rows.map((row) => row.doctorId));

  const update = (index: number, patch: Partial<ServiceDoctorRow>) => onChange(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  const remove = (index: number) => onChange(rows.filter((_, at) => at !== index));
  const add = () => onChange([...rows, { doctorId: '', discountKind: 'percent', value: '0' }]);

  return (
    <FormSection title={tr('billing', 'doctors')} icon={Stethoscope}>
      <div className="sm:col-span-2 space-y-3">
        <p className="text-sm text-muted-foreground">{tr('billing', 'doctors_with_a_discount_on_this_service')}</p>
        {rows.length === 0 && <p className="text-sm text-muted-foreground">{tr('billing', 'no_doctor_discounts_on_this_service')}</p>}
        {rows.map((row, index) => {
          const price = doctorPriceMinor({ discountKind: row.discountKind, discountValue: discountValueOf(row) }, basePriceMinor);
          return (
            <div key={index} className="grid gap-2 rounded-md border border-border/70 p-3 sm:grid-cols-[1fr_auto_auto_auto_auto] sm:items-end">
              <label className="text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">{tr('billing', 'doctor')}</span>
                <Select value={row.doctorId} onChange={(event) => update(index, { doctorId: event.target.value })}>
                  <option value="">{tr('billing', 'choose_a_doctor')}</option>
                  {options
                    .filter((option) => option.id === row.doctorId || !chosen.has(option.id))
                    .map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                </Select>
              </label>
              <div className="text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">{tr('billing', 'kind')}</span>
                <ChoiceButtons<DoctorDiscountKind>
                  size="sm"
                  inline
                  aria-label={tr('billing', 'kind')}
                  value={row.discountKind}
                  onChange={(kind) => {
                    if (kind !== row.discountKind) update(index, { discountKind: kind, value: '0' });
                  }}
                  options={[
                    { value: 'percent', label: tr('billing', 'discount_kind_percent') },
                    { value: 'fixed', label: tr('billing', 'discount_kind_fixed') },
                  ]}
                />
              </div>
              <label className="text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">{tr('billing', 'discount')}</span>
                {row.discountKind === 'percent' ? (
                  <Input type="number" min={0} max={100} step={0.5} value={row.value} onChange={(event) => update(index, { value: event.target.value })} className="tabular w-28" />
                ) : (
                  <Input type="text" inputMode="decimal" dir="ltr" value={row.value} onChange={(event) => update(index, { value: event.target.value })} className="tabular w-32" />
                )}
              </label>
              <div className="text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">{tr('billing', 'price_after_discount')}</span>
                <span className="tabular font-medium">{money(price)}</span>
              </div>
              <Button type="button" variant="ghost" size="icon" aria-label={tr('billing', 'remove')} onClick={() => remove(index)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={clinicians.isLoading}>
          <Plus className="h-4 w-4" /> {tr('billing', 'add_doctor')}
        </Button>
      </div>
    </FormSection>
  );
}
